/**
 * EntityDetailPage — the "Living Record" page for a Company / Topic / Market.
 *
 * URL patterns:
 *   /companies/:slug   → kind="company"
 *   /topics/:slug      → kind="topic"
 *   /markets/:slug     → kind="market"
 *
 * Layout blocks (top-to-bottom):
 *   1. Sticky hero (breadcrumb + icon + name + meta chips + KPI cards)
 *   2. Related entities pill row (co-occurring companies / topics / markets)
 *   3. Filters bar (search + region + topic + source + date range)
 *   4. Timeline grouped by month with infinite scroll
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, ArrowUpRight, ChevronDown, ChevronRight,
  Building2, Tag, MapPin, Newspaper, FileText, TrendingUp,
  Search, X, ExternalLink, FileSearch,
} from "lucide-react";
import PageHeader from "@/layout/PageHeader";
import { fetchers, qk } from "@/lib/queryClient";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import ReasoningCard from "@/components/ReasoningCard";

// ---------------- Tokens ----------------
const T = {
  fg: "#0F1B2D",
  fgMuted: "#3D4B62",
  fgSubtle: "#5B6B82",
  border: "#E5E9F0",
  borderStrong: "#D5DCE6",
  bg: "#F7FAFC",
  surface: "#FFFFFF",
  accent: "#0FA9DB",
  accentSoft: "#E6F4FB",
  accentBorder: "#B8E1F3",
  ring: "rgba(15,169,219,0.14)",
};

// ---------------- Kind mapping ----------------
const KIND = {
  company: { plural: "companies", label: "Companies", Icon: Building2 },
  topic:   { plural: "topics",    label: "Topics",    Icon: Tag },
  market:  { plural: "markets",   label: "Markets",   Icon: MapPin },
};

// Fetchers routed by kind.
const DETAIL_FETCHER = {
  company: (s) => fetchers.companyDetail(s),
  topic:   (s) => fetchers.topicDetail(s),
  market:  (s) => fetchers.marketDetail(s),
};
const DETAIL_QK = {
  company: qk.companyDetail,
  topic:   qk.topicDetail,
  market:  qk.marketDetail,
};
const FEED_FETCHER = {
  company: (s, p) => fetchers.companyArticles(s, p),
  topic:   (s, p) => fetchers.topicArticles(s, p),
  market:  (s, p) => fetchers.marketArticles(s, p),
};
const FEED_QK = {
  company: qk.companyArticles,
  topic:   qk.topicArticles,
  market:  qk.marketArticles,
};

// ---------------- Utils ----------------
function faviconFor(website, name) {
  const host = website
    ? String(website).replace(/^https?:\/\//, "").replace(/\/.*$/, "")
    : `${(name || "").toLowerCase().replace(/[^a-z0-9]/g, "")}.com`;
  return `https://icons.duckduckgo.com/ip3/${host}.ico`;
}
function relativeTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.round(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
}
function monthKey(iso) {
  if (!iso) return "Unknown";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

// ---------------- Hero ----------------
function EntityHero({ kind, detail }) {
  const { Icon, label, plural } = KIND[kind];
  const isPriority = kind === "company"
    ? detail.priority === "critical" || detail.priority === "high" || detail.tier === 1
    : !!detail.is_priority;

  return (
    <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}` }}>
      <div className="mx-auto max-w-6xl px-6 pt-6 pb-5">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[12px]" style={{ color: T.fgSubtle }}>
          <Link
            to={`/${plural === "companies" ? "companies" : "companies"}`}
            className="inline-flex items-center gap-1 hover:underline"
            style={{ color: T.fgMuted }}
            data-testid="entity-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Intelligence Backbone
          </Link>
          <span>/</span>
          <span className="capitalize">{label}</span>
          <span>/</span>
          <span style={{ color: T.fg, fontWeight: 500 }}>{detail.name}</span>
        </div>

        {/* Title row */}
        <div className="mt-4 flex items-start gap-5">
          {/* Icon column */}
          <div
            className="shrink-0 rounded-xl grid place-items-center overflow-hidden"
            style={{
              width: 64, height: 64,
              background: T.accentSoft,
              border: `1px solid ${T.border}`,
            }}
          >
            {kind === "company" ? (
              <img
                src={faviconFor(detail.website, detail.name)}
                alt=""
                width={40} height={40}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : kind === "market" && detail.flag ? (
              <span style={{ fontSize: 34, lineHeight: 1 }} aria-hidden>{detail.flag}</span>
            ) : (
              <Icon className="h-8 w-8" style={{ color: T.accent }} strokeWidth={2} />
            )}
          </div>
          {/* Text column */}
          <div className="flex-1 min-w-0">
            <h1
              data-testid="entity-detail-title"
              style={{ color: T.fg, fontSize: 30, fontWeight: 600, letterSpacing: -0.4, lineHeight: 1.1 }}
            >
              {detail.name}
            </h1>
            {/* Chips */}
            <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
              {kind === "company" && detail.category && (
                <Chip label={detail.category.replaceAll("_", " ")} />
              )}
              {kind === "company" && detail.tier && (
                <Chip label={`Tier ${detail.tier}`} />
              )}
              {kind === "market" && detail.kind_market && (
                <Chip label={detail.kind_market === "region" ? "Region" : "Country"} />
              )}
              {isPriority && <Chip label="PRIORITY" priority />}
            </div>
            {/* One-line description */}
            {detail.description && (
              <p style={{ color: T.fgMuted, fontSize: 14, marginTop: 10, lineHeight: 1.55, maxWidth: 720 }}>
                {detail.description}
              </p>
            )}
          </div>
        </div>

        {/* KPI cards */}
        <EntityMetaCards stats={detail.stats} />
      </div>
    </div>
  );
}

function Chip({ label, priority = false }) {
  const s = priority
    ? { background: T.accentSoft, color: T.accent, border: `1px solid ${T.accentBorder}`, fontWeight: 600 }
    : { background: "#F1F5F9", color: T.fgMuted, border: `1px solid ${T.border}`, fontWeight: 500 };
  return (
    <span
      className="inline-block rounded-full whitespace-nowrap"
      style={{ ...s, fontSize: 11, padding: "3px 10px", letterSpacing: 0.2, textTransform: priority ? "uppercase" : "none" }}
    >
      {label}
    </span>
  );
}

function EntityMetaCards({ stats }) {
  const items = [
    { label: "Articles indexed", value: stats.articles_total },
    { label: "Briefs", value: stats.briefs_count },
    { label: "Sources", value: stats.sources_count },
    { label: "Last mention", value: relativeTime(stats.last_mention_at) || "—" },
    {
      label: "Momentum (WoW)",
      value: stats.momentum_wow == null
        ? "—"
        : `${stats.momentum_wow >= 0 ? "+" : ""}${Math.round(stats.momentum_wow * 100)}%`,
      trend: stats.momentum_wow,
    },
  ];
  return (
    <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {items.map((it) => (
        <div
          key={it.label}
          data-testid={`kpi-${it.label.toLowerCase().replaceAll(" ", "-")}`}
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 10, padding: "12px 14px",
          }}
        >
          <div className="flex items-center gap-1.5">
            <span style={{ color: T.fg, fontSize: 20, fontWeight: 600 }}>
              {it.value ?? 0}
            </span>
            {typeof it.trend === "number" && it.trend !== 0 && (
              <TrendingUp
                className="h-3.5 w-3.5"
                style={{
                  color: it.trend > 0 ? "#0F9F6E" : "#DC2626",
                  transform: it.trend > 0 ? "none" : "rotate(180deg)",
                }}
              />
            )}
          </div>
          <div className="mono mt-1" style={{ color: T.fgSubtle, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" }}>
            {it.label}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------- Related pills ----------------
function RelatedEntities({ related }) {
  const kinds = [
    { kind: "topic",   plural: "topics",    items: related?.topics    || [] },
    { kind: "company", plural: "companies", items: related?.companys  || related?.companies || [] },
    { kind: "market",  plural: "markets",   items: related?.markets   || [] },
  ];
  const all = kinds.flatMap(k => k.items.map(x => ({ ...x, kind: k.kind, plural: k.plural })));
  if (!all.length) return null;
  return (
    <div className="mx-auto max-w-6xl px-6 py-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="mono" style={{ color: T.fgSubtle, fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase" }}>
          Frequently mentioned with
        </span>
        {all.slice(0, 10).map((r) => (
          <Link
            key={`${r.kind}-${r.slug}`}
            to={`/${r.plural}/${r.slug}`}
            className="inline-flex items-center gap-1.5 rounded-full transition-colors"
            style={{
              background: T.accentSoft, color: T.accent,
              border: `1px solid ${T.accentBorder}`,
              padding: "3px 10px", fontSize: 12, fontWeight: 500,
            }}
            data-testid={`related-${r.kind}-${r.slug}`}
          >
            {r.name}
            <span className="mono" style={{ opacity: 0.7, fontSize: 10 }}>{r.count}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ---------------- Filters ----------------
function ArticleFilters({ value, onChange, options }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const hasAny = value.q || value.region || value.topic || value.source || value.date_from || value.date_to;
  return (
    <div
      className="mx-auto max-w-6xl px-6 py-3 flex items-center gap-2 flex-wrap"
      style={{ background: T.bg, borderBottom: `1px solid ${T.border}` }}
    >
      <div className="relative flex-1 min-w-[220px] max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: T.fgSubtle }} />
        <Input
          placeholder="Filter mentions…"
          value={value.q || ""}
          onChange={(e) => set("q", e.target.value)}
          className="pl-9 h-9"
          data-testid="entity-filter-search"
        />
      </div>
      <SelectFilter
        placeholder="Region"
        value={value.region || ""}
        options={options.regions}
        onChange={(v) => set("region", v)}
        testid="entity-filter-region"
      />
      <SelectFilter
        placeholder="Source"
        value={value.source || ""}
        options={options.sources}
        onChange={(v) => set("source", v)}
        testid="entity-filter-source"
      />
      {hasAny && (
        <button
          type="button"
          onClick={() => onChange({})}
          className="inline-flex items-center gap-1 text-xs font-medium h-9 px-3 rounded-md"
          style={{ color: T.fgMuted, background: T.surface, border: `1px solid ${T.border}` }}
          data-testid="entity-filter-reset"
        >
          <X className="h-3 w-3" /> Reset
        </button>
      )}
    </div>
  );
}

function SelectFilter({ placeholder, value, options, onChange, testid }) {
  return (
    <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
      <SelectTrigger className="h-9 w-[150px]" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all">All {placeholder.toLowerCase()}s</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------------- Article card ----------------
function ArticleCard({ a }) {
  return (
    <article
      className="group flex gap-4"
      style={{
        background: T.surface, border: `1px solid ${T.border}`,
        borderRadius: 10, padding: 14,
        transition: "border-color 150ms ease, box-shadow 150ms ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.accentBorder; e.currentTarget.style.boxShadow = "0 4px 12px rgba(15,27,45,0.06)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}
      data-testid={`timeline-article-${a.id}`}
    >
      <div
        className="shrink-0 rounded-lg overflow-hidden grid place-items-center"
        style={{ width: 96, height: 96, background: T.accentSoft }}
      >
        {a.image_url
          ? <img src={a.image_url} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
          : <Newspaper className="h-8 w-8" style={{ color: T.accent, opacity: 0.5 }} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <h3 style={{ color: T.fg, fontSize: 15, fontWeight: 600, lineHeight: 1.35 }} className="flex-1">
            {a.title}
          </h3>
          {typeof a.priority_score === "number" && a.priority_score > 0 && (
            <span
              className="mono uppercase tracking-wider shrink-0"
              style={{
                color: T.accent, background: T.accentSoft,
                border: `1px solid ${T.accentBorder}`,
                fontSize: 10, fontWeight: 600,
                padding: "1px 6px", borderRadius: 4,
              }}
            >
              ★ {a.priority_score}
            </span>
          )}
        </div>
        <p style={{ color: T.fgMuted, fontSize: 13, marginTop: 4, lineHeight: 1.5 }} className="line-clamp-2">
          {a.paragraph}
        </p>
        <div className="mt-2.5 flex items-center gap-2 flex-wrap" style={{ fontSize: 11 }}>
          {a.origin === "wingman" && (
            <span
              data-testid="article-origin-wingman"
              style={{
                color: "#7FFFC4", background: "rgba(4,120,87,0.16)",
                border: "1px solid rgba(4,120,87,0.55)",
                fontSize: 10, fontWeight: 600,
                padding: "1px 6px", borderRadius: 4,
                letterSpacing: 0.4, textTransform: "uppercase",
              }}
              title="Curated by Wingman via WhatsApp"
            >
              Wingman
            </span>
          )}
          {a.source_name && <span style={{ color: T.fgSubtle, fontWeight: 500 }}>{a.source_name}</span>}
          {a.published_at && (
            <><span style={{ color: T.borderStrong }}>·</span>
              <span className="mono" style={{ color: T.fgSubtle }}>{relativeTime(a.published_at)}</span></>
          )}
          {a.topic_tags?.slice(0, 3).map((t) => (
            <span
              key={t}
              className="mono"
              style={{ background: "#F1F5F9", color: T.fgMuted, border: `1px solid ${T.border}`, borderRadius: 999, padding: "1px 7px", fontSize: 10 }}
            >
              {t}
            </span>
          ))}
          <div className="ml-auto flex items-center gap-3">
            {a.url_original && (
              <a
                href={a.url_original}
                target="_blank"
                rel="noreferrer"
                style={{ color: T.accent, fontWeight: 500 }}
                className="inline-flex items-center gap-1 hover:underline"
                data-testid={`timeline-article-link-${a.id}`}
              >
                <ExternalLink className="h-3 w-3" /> Read source
              </a>
            )}
            {a.brief_id && (
              <Link
                to={`/daily-briefs/${a.brief_id}#article-${a.id}`}
                style={{ color: T.fgMuted, fontWeight: 500 }}
                className="inline-flex items-center gap-1 hover:underline"
                data-testid={`timeline-article-brief-${a.id}`}
              >
                <FileText className="h-3 w-3" /> See in brief
              </Link>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

// ---------------- Timeline ----------------
function ArticleTimeline({ groups, collapsedMap, toggle }) {
  return (
    <div className="space-y-8">
      {Object.entries(groups).map(([month, list], idx) => {
        const collapsed = collapsedMap[month] === true;   // by default all months open
        return (
          <section key={month} data-testid={`timeline-month-${month.replace(/\s/g, "-").toLowerCase()}`}>
            <button
              type="button"
              onClick={() => toggle(month)}
              className="flex items-center gap-2 mb-3 w-full text-left"
              style={{ color: T.fgMuted }}
            >
              {collapsed
                ? <ChevronRight className="h-4 w-4" />
                : <ChevronDown className="h-4 w-4" />}
              <span style={{ color: T.fg, fontSize: 18, fontWeight: 600, letterSpacing: -0.2 }}>
                {month}
              </span>
              <span className="mono" style={{ color: T.fgSubtle, fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase" }}>
                · {list.length} mention{list.length === 1 ? "" : "s"}
              </span>
            </button>
            {!collapsed && (
              <div className="space-y-3">
                {list.map((a) => <ArticleCard key={a.id} a={a} />)}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

// ---------------- Empty state ----------------
function EmptyState() {
  return (
    <div
      className="mx-auto max-w-md text-center py-16 px-6"
      data-testid="entity-empty"
      style={{ color: T.fgSubtle }}
    >
      <div
        className="mx-auto mb-4 rounded-full grid place-items-center"
        style={{ width: 64, height: 64, background: T.accentSoft }}
      >
        <FileSearch className="h-7 w-7" style={{ color: T.accent }} strokeWidth={1.8} />
      </div>
      <h3 style={{ color: T.fg, fontSize: 15, fontWeight: 600 }}>No mentions yet</h3>
      <p style={{ fontSize: 13, marginTop: 6, lineHeight: 1.55 }}>
        This entity will populate automatically as new daily briefs are generated.
      </p>
    </div>
  );
}

// ---------------- Skeletons ----------------
function HeroSkeleton() {
  return (
    <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}` }}>
      <div className="mx-auto max-w-6xl px-6 pt-6 pb-5">
        <Skeleton className="h-3 w-64" />
        <div className="mt-4 flex items-start gap-5">
          <Skeleton className="h-16 w-16 rounded-xl" />
          <div className="flex-1 space-y-2.5">
            <Skeleton className="h-8 w-72" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <div className="mt-5 grid grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );
}
function TimelineSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex gap-4" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
          <Skeleton className="h-24 w-24 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------- Main page ----------------
export default function EntityDetailPage({ kind }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const scrollSentinelRef = useRef(null);

  // Debounce the free-text query so we don't refetch on every keypress.
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q || ""), 300);
    return () => clearTimeout(t);
  }, [filters.q]);

  const effectiveFilters = { ...filters, q: debouncedQ };

  const detailQuery = useQuery({
    queryKey: DETAIL_QK[kind](slug),
    queryFn:  () => DETAIL_FETCHER[kind](slug),
    staleTime: 60_000,
  });

  const feedQuery = useInfiniteQuery({
    queryKey: FEED_QK[kind](slug, effectiveFilters),
    queryFn: ({ pageParam = 0 }) => FEED_FETCHER[kind](slug, {
      limit: 20, offset: pageParam,
      region: effectiveFilters.region, topic: effectiveFilters.topic,
      source: effectiveFilters.source, q: effectiveFilters.q,
    }),
    getNextPageParam: (last) => {
      const meta = last?.meta || {};
      const next = (meta.offset || 0) + (meta.limit || 20);
      return next < (meta.total || 0) ? next : undefined;
    },
    initialPageParam: 0,
    staleTime: 60_000,
  });

  const articles = useMemo(
    () => (feedQuery.data?.pages || []).flatMap((p) => p.data || []),
    [feedQuery.data],
  );
  const groups = useMemo(() => {
    const out = {};
    for (const a of articles) {
      const k = monthKey(a.published_at);
      (out[k] = out[k] || []).push(a);
    }
    return out;
  }, [articles]);

  // Infinite-scroll sentinel.
  useEffect(() => {
    const el = scrollSentinelRef.current;
    if (!el || !feedQuery.hasNextPage || feedQuery.isFetchingNextPage) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) feedQuery.fetchNextPage();
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [feedQuery]);

  // Filter option lists derived from the loaded articles (dynamic).
  const options = useMemo(() => {
    const regions = new Set();
    const sources = new Set();
    for (const a of articles) {
      if (a.region) regions.add(a.region);
      if (a.source_name) sources.add(a.source_name);
    }
    return {
      regions: Array.from(regions).sort().map((v) => ({ value: v, label: v.replaceAll("_", " ") })),
      sources: Array.from(sources).sort().map((v) => ({ value: v, label: v })),
    };
  }, [articles]);

  const toggleMonth = (m) => setCollapsed((c) => ({ ...c, [m]: !c[m] }));

  const notFound = detailQuery.isError;
  if (notFound) {
    return (
      <div className="mx-auto max-w-6xl px-6 py-10">
        <PageHeader title="Not found" subtitle={`${kind} '${slug}' does not exist.`} />
        <button
          type="button"
          onClick={() => navigate("/companies")}
          className="mt-4 inline-flex items-center gap-1.5 text-sm"
          style={{ color: T.accent }}
        >
          <ArrowLeft className="h-4 w-4" /> Back to Intelligence Backbone
        </button>
      </div>
    );
  }

  return (
    <div style={{ background: T.bg, minHeight: "100vh" }} data-testid={`entity-detail-${kind}`}>
      {/* Sticky hero */}
      <div className="sticky top-0 z-30" style={{ background: T.surface }}>
        {detailQuery.isPending
          ? <HeroSkeleton />
          : <EntityHero kind={kind} detail={detailQuery.data} />}
      </div>

      {/* Related pills */}
      {!detailQuery.isPending && (
        <RelatedEntities related={detailQuery.data?.related} />
      )}

      {/* Sprint 1 · Turn B — Reasoning card (top of body, above filters) */}
      <div className="mx-auto max-w-6xl px-6 pt-6">
        <ReasoningCard kind={kind} slug={slug} />
      </div>

      {/* Filters bar */}
      <ArticleFilters
        value={filters}
        onChange={setFilters}
        options={options}
      />

      {/* Timeline */}
      <div className="mx-auto max-w-6xl px-6 py-6">
        {feedQuery.isPending ? (
          <TimelineSkeleton />
        ) : articles.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <ArticleTimeline groups={groups} collapsedMap={collapsed} toggle={toggleMonth} />
            {/* Infinite scroll sentinel */}
            {feedQuery.hasNextPage && (
              <div ref={scrollSentinelRef} className="mt-6">
                <TimelineSkeleton />
              </div>
            )}
            {!feedQuery.hasNextPage && (
              <div
                className="mt-8 text-center mono"
                style={{ color: T.fgSubtle, fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase" }}
              >
                — End of timeline · {articles.length} mention{articles.length === 1 ? "" : "s"} —
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
